import { Box, Center, HStack, ModalBody, ModalFooter, Text, VStack } from '@chakra-ui/react';
import { FC } from 'react';

import { StarFilledIcon } from '../../../../../assets/icons';
import { ButtonRow } from '../../../../../components/Buttons';
import { PrimaryButton } from '../../../../../components/Buttons/PrimaryButton';
import { CenteredLoadingIndicator } from '../../../../../components/LoadingIndicator';
import { BaseModal } from '../../../../../components/Modals/BaseModal';
import ServerValidationBox from '../../../../../components/ServerValidationBox';
import { Tag } from '../../../../../components/TagComponent';
import { AddressBook } from '../../../../../generated';
import useTranslationComponent from '../../../../../hooks/useTranslationComponent';

import { useSelectPharmacyOptions } from '../../hooks/useSelectPharmacyOptions';

import { PharmacyCard } from './PharmacyCard';
import { SelectPharmacyOptionsCard } from './SelectPharmacyOptionsCard';

type SelectPharmacyOptionsModalProps = {
  isModalOpen: boolean;
  onCloseModal: () => void;
  patientID: string;
  onChangeSendCopyToPatient: (value: boolean) => void;
  onOpenAddressBook: () => void;
  selectedAddress?: AddressBook;
  isLoading?: boolean;
  onChangeAddress: (value: AddressBook) => void;
  sendCopyToPatient: boolean;
};

const SelectPharmacyOptionsModal: FC<SelectPharmacyOptionsModalProps> = (props) => {
  const { t } = useTranslationComponent(['phr']);
  const controller = useSelectPharmacyOptions({
    onCloseModal: props.onCloseModal,
    patientID: props.patientID,
    onChangeSendCopyToPatient: props.onChangeSendCopyToPatient,
    selectedAddress: props.selectedAddress,
    onChangeAddress: props.onChangeAddress,
    isLoading: props.isLoading,
    sendCopyToPatient: props.sendCopyToPatient,
  });

  return (
    <BaseModal
      isOpen={props.isModalOpen}
      size="xl"
      onDismiss={controller.isLoadingPreferredPharmacy ? undefined : props.onCloseModal}
      title={
        <Box mb="3" pr="4" width="100%">
          <Text textTransform="uppercase" color="#3E1191" mb="2">
            {t("Select Patient's Preferred Pharmacy")}
          </Text>
          <Text fontSize="14px" color="#3E1191">
            {t('This is where the prescription will be sent to')}
          </Text>
        </Box>
      }
    >
      {controller.preferredPharmacyError ? (
        <ServerValidationBox message={controller.preferredPharmacyError} />
      ) : controller.isLoadingPreferredPharmacy ? (
        <CenteredLoadingIndicator />
      ) : (
        <VStack height="100%" alignItems="stretch" overflowY="auto">
          <ModalBody flex="1" overflowY="auto">
            <VStack overflowY="auto" height="100%" width="100%" spacing="4">
              <SelectPharmacyOptionsCard containerProps={{ _hover: undefined, minH: '100px' }}>
                <VStack
                  alignItems="stretch"
                  height="100%"
                  width="100%"
                  justifyContent={controller.hasSelectedPharmacy ? 'flex-start' : 'center'}
                >
                  {controller.hasSelectedPharmacy && (
                    <HStack justifyContent="flex-end">
                      <Tag
                        text={`${
                          controller.isShowingPreferredPharmacy ? 'Preferred' : 'Selected'
                        } Pharmacy`}
                        iconComponent={
                          controller.isShowingPreferredPharmacy && (
                            <StarFilledIcon fontSize="12px" />
                          )
                        }
                        textProps={{ fontSize: '12px' }}
                        containerProps={{
                          bgColor: controller.isShowingPreferredPharmacy
                            ? 'rgb(255, 194, 0)'
                            : '#b1e8e3',
                        }}
                      />
                    </HStack>
                  )}
                  <Center height="100%" justifyContent="flex-start">
                    {props.selectedAddress && props.selectedAddress.name ? (
                      <PharmacyCard
                        imageID={props.selectedAddress?.imageDetailID}
                        name={props.selectedAddress?.name}
                      />
                    ) : (
                      <Text fontSize="14px">{t('No Pharmacy Selected')}</Text>
                    )}
                  </Center>
                </VStack>
              </SelectPharmacyOptionsCard>

              <SelectPharmacyOptionsCard
                containerProps={{
                  role: 'button',
                  onClick: props.onOpenAddressBook,
                }}
                showRightArrow
              >
                <Center height="100%" justifyContent="flex-start">
                  <Text fontSize="14px">
                    {t(`Select a ${controller.hasSelectedPharmacy ? 'Different' : ''} Pharmacy`)}
                  </Text>
                </Center>
              </SelectPharmacyOptionsCard>

              {!controller.hasSelectedPharmacy && (
                <SelectPharmacyOptionsCard
                  containerProps={{
                    role: 'button',
                    onClick: controller.toggleSelectMedicationFirst,
                  }}
                  isSelected={controller.selectMedicationFirst}
                  showCheckbox
                  checkboxProps={{
                    isChecked: controller.selectMedicationFirst,
                  }}
                >
                  <Center height="100%" justifyContent="flex-start">
                    <Box>
                      <Text fontSize="14px">{t('Select Medications First')}</Text>
                      <Text fontSize="14px">
                        ({t('You may choose a Pharmacy to send to later')})
                      </Text>
                    </Box>
                  </Center>
                </SelectPharmacyOptionsCard>
              )}

              {/* <SelectPharmacyOptionsCard
                containerProps={{
                  role: 'button',
                  onClick: () => props.onChangeSendCopyToPatient(!props.sendCopyToPatient),
                }}
                isSelected={props.sendCopyToPatient}
                showCheckbox
                checkboxProps={{
                  isChecked: props.sendCopyToPatient,
                }}
              >
                <Center height="100%" justifyContent="flex-start">
                  <Text fontSize="14px">Send prescription directly to Patient</Text>
                </Center>
              </SelectPharmacyOptionsCard> */}
            </VStack>
          </ModalBody>
          <ModalFooter pt="0">
            <ButtonRow>
              <PrimaryButton
                isDisabled={controller.isContinueDisabled}
                onClick={controller.onContinue}
              >
                {t('Continue')}
              </PrimaryButton>
            </ButtonRow>
          </ModalFooter>
        </VStack>
      )}
    </BaseModal>
  );
};

export { SelectPharmacyOptionsModal };
